import {
  selectBrand,
  selectFavoriteFilter,
  selectTag,
  setSearchQuery,
} from "@shared/actions/games";
import { Icon } from "@shared/components";
import {
  SHOW_ALL,
  TAG_FAVORITE,
  TAG_NAME_AVIATOR,
  TAG_NAME_CHINESE,
  TAG_NAME_CHRISTMAS,
  TAG_NAME_HALLOWEEN,
  TAG_NAME_NEW,
} from "@shared/constants/game";
import { useIsFavoriteFilterActive } from "@shared/hooks/games/games";
import { useComputedTags, useCurrentTagId } from "@shared/hooks/games/tags";
import { useIsAuth } from "@shared/hooks/user";
import {
  TAG_RECOMMENDED,
  useRecommendationGames,
} from "@shared/modules/recommended-games";
import { filterByCountOfGames } from "@shared/utils";
import cx from "classnames";
import { lowerCase, throttle } from "lodash-es";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";

const TAG_CLASS = "tabset__item tabset__item__section";
const SELECTED_CLASS = "tabset__item--selected";
const DISABLE_CLASS = "tabset__item--disabled";
const IMG_CLASS = "tabset__item--img";
const IMG_CLASS_HALLOWEEN = "tabset__item--img-halloween";
const IMG_CLASS_CHINESE = "tabset__item--img-chinese";
const IMG_CLASS_CHRISTMAS = "tabset__item--img-christmas";

const THROTTLE_LIMIT = 1000;

const Tags = ({ isSearchShown = true }) => {
  const dispatch = useDispatch();
  const isAuth = useIsAuth();
  const intl = useIntl();

  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [throttledSearchQuery, setThrottledSearchQuery] = useState("");
  const isFavoriteFilterActive = useIsFavoriteFilterActive();
  const { recommendationGames, handleRecommendationFilter } =
    useRecommendationGames();

  const throttledSearchQueryReference = useRef(
    throttle((searchQuery) => {
      dispatch(setSearchQuery(lowerCase(searchQuery)));
    }, THROTTLE_LIMIT)
  );

  useEffect(
    () => throttledSearchQueryReference.current(throttledSearchQuery),
    [throttledSearchQuery]
  );

  const resetSearchQuery = useCallback(() => {
    setThrottledSearchQuery("");
    setIsSearchExpanded(false);
  }, [setThrottledSearchQuery]);

  const handleFocus = useCallback(() => {
    setIsSearchExpanded(true);
  }, []);

  const handleBlur = useCallback(() => {
    if (!throttledSearchQuery) {
      setIsSearchExpanded(false);
    }
  }, [throttledSearchQuery]);

  const currentTagId = useCurrentTagId();
  const computedTags = useComputedTags();

  const handleTagClick = useCallback(
    (event) => {
      const id = Number.parseInt(event.target.dataset.id, 10);
      dispatch(selectTag(id));
      dispatch(selectFavoriteFilter(false));
    },
    [dispatch]
  );

  const handleFavoriteFilterClick = useCallback(() => {
    dispatch(selectBrand(SHOW_ALL));
    dispatch(selectTag(TAG_FAVORITE));
    dispatch(selectFavoriteFilter(true));
  }, [dispatch]);

  const isHidden = useMemo(
    () =>
      filterByCountOfGames(computedTags).length <= 0 && !isFavoriteFilterActive,
    [computedTags, isFavoriteFilterActive]
  );

  return (
    <div className="tabset tabset--menu" role="list">
      <div style={{ flex: 1 }} role="presentation" />
      <div
        data-id={-1}
        className={cx(TAG_CLASS, {
          [SELECTED_CLASS]: currentTagId === -1 && !isFavoriteFilterActive,
        })}
        onClick={handleTagClick}
        role="listitem"
      >
        {intl.formatMessage({ id: "all", defaultMessage: "All" })}
      </div>
      <div
        data-id={0}
        className={cx(TAG_CLASS, {
          [SELECTED_CLASS]: isFavoriteFilterActive,
          [DISABLE_CLASS]: !isAuth,
        })}
        onClick={handleFavoriteFilterClick}
        role="listitem"
      >
        {intl.formatMessage({ id: "boilerplate.components.Tag.Favorite" })}
      </div>
      {recommendationGames?.length ? (
        <div
          data-id={TAG_RECOMMENDED}
          className={cx(TAG_CLASS, {
            [SELECTED_CLASS]: currentTagId === TAG_RECOMMENDED,
            [DISABLE_CLASS]: !isAuth,
          })}
          onClick={handleRecommendationFilter}
          role="listitem"
        >
          {intl.formatMessage({ id: "boilerplate.recommendations" })}
        </div>
      ) : null}
      {!isHidden &&
        computedTags.map((tag) => {
          return (
            <div
              key={tag.id}
              className={cx(TAG_CLASS, {
                [SELECTED_CLASS]: currentTagId === tag.id,
                [DISABLE_CLASS]: !tag.games_count,
              })}
              onClick={handleTagClick}
              data-id={tag.id}
              role="listitem"
            >
              <TagItem name={tag.name} />
            </div>
          );
        })}

      {isSearchShown ? (
        <div className={cx("search", { "search--expanded": isSearchExpanded })}>
          <input
            type="text"
            className="search__input"
            placeholder={
              intl.formatMessage({ id: "search", defaultMessage: "Search" }) +
              "..."
            }
            value={throttledSearchQuery}
            onChange={(event) => setThrottledSearchQuery(event.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <div
            className="search__reset-button"
            onClick={resetSearchQuery}
            role="button"
            aria-label="search reset"
          />
        </div>
      ) : (
        <div style={{ flex: 1 }} role="presentation" />
      )}
    </div>
  );
};

export default memo(Tags);

export const TagItem = ({ name }) => {
  if (name === TAG_NAME_AVIATOR) {
    return <Icon className={IMG_CLASS} name="aviator" />;
  }

  if (name === TAG_NAME_CHRISTMAS) {
    return <Icon className={IMG_CLASS_CHRISTMAS} name="christmas" />;
  }

  if (name === TAG_NAME_HALLOWEEN) {
    return <Icon className={IMG_CLASS_HALLOWEEN} name="halloween" />;
  }

  if (name === TAG_NAME_CHINESE) {
    return <Icon className={IMG_CLASS_CHINESE} name="chineseTag" />;
  }

  if (name === TAG_NAME_NEW) {
    return name.toUpperCase();
  }

  return name;
};
